import { useState } from "react";
import clsx from "clsx";
import { useEmpresa } from "@/contexts/EmpresaContext";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmpresaBadge } from "@/components/ui/EmpresaBadge";

export const EmpresaSelector = () => {
  const { empresas, empresaActiva, loading, select } = useEmpresa();
  const [open, setOpen] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [changing, setChanging] = useState(false);
  const current = empresas.find((e) => e.id === empresaActiva) ?? null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-slate-100 px-3 py-1.5">
        <Skeleton className="h-2 w-2 rounded-full" />
        <Skeleton className="h-3 w-28" />
      </div>
    );
  }

  if (!current) return null;

  return (
    <>
      <div className="flex items-center">
        <EmpresaBadge
          razonSocial={current.razon_social}
          nit={current.nit}
          onClick={() => setOpen(true)}
        />
      </div>

      <ConfirmDialog
        open={open}
        onCancel={() => {
          setOpen(false);
          setPendingId(null);
        }}
        onConfirm={async () => {
          if (pendingId === null) return;
          setChanging(true);
          try {
            await select(pendingId);
            setOpen(false);
            setPendingId(null);
          } finally {
            setChanging(false);
          }
        }}
        loading={changing}
        title="Seleccionar empresa"
        confirmLabel="Cambiar"
      >
        <ul className="mt-3 max-h-64 space-y-1 overflow-y-auto">
          {empresas.map((empresa) => (
            <li key={empresa.id}>
              <button
                type="button"
                onClick={() => setPendingId(empresa.id)}
                aria-pressed={pendingId === empresa.id}
                aria-current={empresa.id === empresaActiva ? "true" : undefined}
                className={clsx(
                  "flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors",
                  pendingId === empresa.id
                    ? "bg-accent-blue/15 text-accent-blue"
                    : "hover:bg-slate-100",
                  empresa.id === empresaActiva && "font-semibold",
                )}
              >
                <span className="truncate">{empresa.razon_social}</span>
                <span className="ml-2 text-xs text-ink-secondary">{empresa.nit}</span>
              </button>
            </li>
          ))}
        </ul>
      </ConfirmDialog>
    </>
  );
};
